import React from 'react';
import Svg, { Defs, LinearGradient, Stop, Text as SvgText } from 'react-native-svg';

import { useSvgId } from '../../../utils/hooks/useSvgId';

// The redesign's "Gold/Text Gradient" - same stops TopJackpotBanner paints JACKPOT with.
// RN's Text cannot fill glyphs with a gradient, so the label is drawn twice: once in the
// dark shadow colour, once with the gradient a hair above it.
const SHADOW_COLOR = '#421000';
const STOPS: Array<[string, string]> = [
	['0', '#FFF5B9'],
	['0.1543', '#FFEE82'],
	['0.5493', '#F4AE2B'],
	['1', '#D18003'],
];

type Props = {
	text: string;
	width: number;
	height: number;
	fontSize: number;
	fontFamily?: string;
	fontWeight?: string;
	/** Forces the glyphs to span exactly this width - keeps long labels inside the card. */
	textLength?: number;
	/** Where the glyphs sit in the box. Defaults to the centred cards / banners use. */
	align?: 'center' | 'left';
	letterSpacing?: number;
	/** Leave off the dark under-copy, for labels the design draws without a shadow. */
	shadow?: boolean;
	/**
	 * Runs the gradient over a taller block this line is part of, `top` being where this line's
	 * box starts in it. A wrapped title then goes pale on its first line and orange on its last,
	 * as the file draws it, instead of repeating the full gradient on every line.
	 */
	gradientSpan?: { top: number; height: number };
};

export const GoldGradientText = ({
	text,
	width,
	height,
	fontSize,
	fontFamily = 'Sofia Sans',
	fontWeight = '700',
	textLength,
	align = 'center',
	letterSpacing,
	shadow = true,
	gradientSpan,
}: Props) => {
	const gradientId = useSvgId('goldText');
	const baseline = height / 2 + fontSize * 0.35;
	const anchorX = align === 'left' ? 0 : width / 2;
	const shared = {
		fontFamily,
		fontSize,
		fontWeight,
		textAnchor: align === 'left' ? ('start' as const) : ('middle' as const),
		...(letterSpacing != null ? { letterSpacing } : undefined),
		...(textLength != null ? { textLength, lengthAdjust: 'spacingAndGlyphs' as const } : undefined),
	};

	return (
		<Svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
			<Defs>
				<LinearGradient
					id={gradientId}
					{...(gradientSpan
						? {
								gradientUnits: 'userSpaceOnUse' as const,
								x1: 0,
								y1: -gradientSpan.top,
								x2: 0,
								y2: gradientSpan.height - gradientSpan.top,
							}
						: { x1: '0', y1: '0', x2: '0', y2: '1' })}
				>
					{STOPS.map(([offset, color]) => (
						<Stop key={offset} offset={offset} stopColor={color} />
					))}
				</LinearGradient>
			</Defs>
			{shadow ? (
				<SvgText {...shared} x={anchorX} y={baseline + 0.8} fill={SHADOW_COLOR}>
					{text}
				</SvgText>
			) : null}
			<SvgText {...shared} x={anchorX} y={baseline} fill={`url(#${gradientId})`}>
				{text}
			</SvgText>
		</Svg>
	);
};

export default GoldGradientText;
